import React, { useState, useEffect } from 'react';
import { Table } from 'reactstrap';
import DownloadOutlineIcon from 'mdi-react/DownloadOutlineIcon';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';
import { Parser } from 'json2csv';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';
import useConfig from '../../util/useConfig';
import Pagination from '../../shared/components/pagination/Pagination';
import Loading from '../../shared/components/custom/Loading';
import FormCard from '../../shared/components/custom/FormCard';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';

const MAX_STARS = 3;

const initials = (first, last) =>
  [first, last]
    .filter(Boolean)
    .map((name) => name[0].toUpperCase())
    .join('') || '?';

const formatPhone = (phone) => {
  if (!phone || phone === 'null' || phone === 'undefined') return '-';
  return phone.startsWith('+') ? phone : `+${phone}`;
};

const FanRating = ({ match }) => {
  const config = useConfig();
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState([]);
  const [totalRatings, setTotalRatings] = useState(undefined);
  const [data, setData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [topPlayers, setTopPlayers] = useState(null);

  const t = partial('Result');
  const s = partial('shared');
  const mc = partial('matchCenter');

  const setDataToObject = (rating, userID) => {
    return rating.data.reduce((acc, curr) => {
      const index = userID.findIndex((user) => {
        return curr.fanrating_userId === user.userCloudId;
      });
      return index === -1
        ? acc
        : [
            ...acc,
            {
              id: userID[index].userCloudId,
              name: `${userID[index].firstname} ${userID[index].lastname}`,
              phoneNumber: userID[index].phoneNumber,
              email: userID[index].email,
              votedFor: curr.player_fullname,
              uniqueID: curr.fanrating_id,
              stars: curr.fanrating_rating,
            },
          ];
    }, []);
  };

  const getFanRating = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const currentTeam = config.teams.find(
        (team) =>
          team.id === match?.homeTeam.id || team.id === match?.awayTeam.id
      );
      const rating = await Api.clubadmin.match.getFanRating(
        match.id,
        currentTeam.id,
        page,
        pageSize
      );

      const getAllRating = await Api.clubadmin.match.getTopFanRating(
        match.id,
        currentTeam.id
      );

      if (getAllRating.data.length > 0) {
        const sortAllRating = getAllRating.data.sort(
          (a, b) => b.rating - a.rating
        );
        // Fewer than three players may have votes; only show those that do.
        setTopPlayers(sortAllRating.slice(0, 3));
      }

      const userRating = await Api.clubadmin.match.getUserRating(
        match.id,
        currentTeam.id
      );

      const result = userRating.data.map((user) => user.userId);
      const { data: userID } = await Api.users.getUsersByCloudIds(result);
      setData(rating.data);
      setUserData(userID);

      const tableData = setDataToObject(rating.data, userID);
      setTotalRatings(rating?.data.total);
      setRatings(tableData);

      setLoading(false);
    } catch {
      toast.error(s('somethingWentWrong'));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (match) {
      getFanRating();
    }
  }, [match]);

  const renderStars = (stars) => (
    <span className="fan-stars" aria-label={`${stars} ${t('stars')}`}>
      {Array.from({ length: MAX_STARS }, (_, i) => (
        <span key={i} className={i < stars ? 'fan-stars__on' : ''}>
          ★
        </span>
      ))}
    </span>
  );

  const renderFanRating = () => {
    if (!ratings.length) {
      return (
        <tr>
          <td colSpan={4} className="text-muted text-center py-4">
            {t('noFanVotes')}
          </td>
        </tr>
      );
    }
    return ratings.map((rating) => (
      <tr key={`renderFanRating-${rating?.uniqueID}`}>
        <td className="table-stack__primary">
          {rating?.name}
          <small className="text-muted d-block">{rating?.email || '-'}</small>
        </td>
        <td data-label={s('phonenumber')}>
          {formatPhone(rating?.phoneNumber)}
        </td>
        <td data-label={t('votedFor')}>{rating?.votedFor}</td>
        <td data-label={t('stars')}>{renderStars(rating?.stars)}</td>
      </tr>
    ));
  };

  const onChangePageSystem = (pager) => {
    if (match) {
      getFanRating(pager.currentPage, pager.pageSize);
    }
  };

  const exportFanRating = async () => {
    setLoading(true);
    try {
      const page = 1;
      const pageSize = 10;
      const currentTeam = config.teams.find(
        (team) =>
          team.id === match?.homeTeam.id || team.id === match?.awayTeam.id
      );

      const allRating = await Api.clubadmin.match.getAllRating(
        match.id,
        currentTeam.id,
        page,
        pageSize,
        true
      );

      const tableData = setDataToObject(allRating, userData);

      if (tableData.length <= 0) {
        toast.error(t('noVotesToDownload'));
        setLoading(false);
        return;
      }
      const fields = [
        { label: s('name'), value: 'name' },
        { label: s('phonenumber'), value: 'phoneNumber' },
        { label: s('email'), value: 'email' },
        { label: t('votedFor'), value: 'votedFor' },
        { label: t('stars'), value: 'stars' },
      ];
      const json2csvParser = new Parser({ fields });
      const csv = json2csvParser.parse(
        tableData.map((row) => ({
          ...row,
          phoneNumber: formatPhone(row.phoneNumber),
        }))
      );
      const fileName = `${match?.homeTeam.shortName} - ${match?.awayTeam.shortName}`;
      fileDownload(`\uFEFF${csv}`, `${fileName}_FanRating.csv`);
      setLoading(false);
    } catch {
      setLoading(false);
      toast.error(s('somethingWentWrong'));
    }
  };

  return (
    <>
      <Loading loading={loading} />
      {topPlayers?.length > 0 && (
        <div className="fan-podium">
          {topPlayers.map((player, index) => (
            <div className="fan-podium__card" key={player?.playerId || index}>
              <span className={`mvp-rank mvp-rank--${index + 1}`}>
                {index + 1}
              </span>
              {player?.player_imageUrl ? (
                <img
                  className="fan-podium__photo"
                  src={player.player_imageUrl}
                  alt=""
                />
              ) : (
                <span className="fan-podium__photo" aria-hidden>
                  {initials(player?.player_firstname, player?.player_lastname)}
                </span>
              )}
              <div className="fan-podium__text">
                <span className="fan-podium__name">
                  {[player?.player_firstname, player?.player_lastname]
                    .filter(Boolean)
                    .join(' ')}
                  {player?.player_jerseyNumber != null && (
                    <span className="mvp-player__jersey">
                      #{player.player_jerseyNumber}
                    </span>
                  )}
                </span>
                <span className="fan-podium__votes">
                  {player?.votes} {t('votes').toLowerCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      <FormCard
        title={
          <>
            {mc('fanRating')}
            {!!totalRatings && (
              <span className="form-card__count">
                {totalRatings} {t('votes').toLowerCase()}
              </span>
            )}
          </>
        }
        actions={
          <SquareIconButton
            icon={<DownloadOutlineIcon />}
            title={t('exportFanRating')}
            id="export-fan-rating"
            disabled={!ratings.length}
            onClick={exportFanRating}
          />
        }
        bodyClassName="form-card__body--flush"
      >
        <Table responsive striped className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th>{s('name')}</th>
              <th>{s('phonenumber')}</th>
              <th>{t('votedFor')}</th>
              <th>{t('stars')}</th>
            </tr>
          </thead>
          <tbody>{match && renderFanRating()}</tbody>
        </Table>
        {data.total > 0 && (
          <div className="stats-filters__pagination">
            <Pagination
              items={data.total}
              pageSize={10}
              onChangePage={onChangePageSystem}
            />
          </div>
        )}
      </FormCard>
    </>
  );
};

export default FanRating;
